import { _js } from '@ifixit/localize';
import { Validation } from 'Shared/utils';
import { Recaptcha } from 'Shared/recaptcha';
import { setHideSubBox, onHideSubBoxChange } from 'Shared/ifixit_store';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { querySelectorAllWithShadowed } from 'Shared/utils/shadow-wrapper';

declare const App: {
   recaptchaToken: string;
};

declare const Request: {
   AjaxIO: new (
      action: string,
      options: { onSuccess: (response: SignupResponse) => void }
   ) => { send: (email: string, recaptchaResponse: string) => Promise<SignupResponse> };
};

declare const fbq: (action: string, event: string, data: { content_name: string }) => void;

type SignupResponse = string | { result: string };

interface MooToolsElement extends Element {
   addClass(className: string): void;
   removeClass(className: string): void;
   set(property: string, value: string): void;
   hide(): void;
}

class NewsLetter {
   protected el!: MooToolsElement;

   constructor(el: Element | null) {
      if (!el) {
         return;
      }

      this.el = el as MooToolsElement;
      const form = this._getForm();
      if (form) {
         form.addEventListener('submit', this._onSubmit.bind(this));
      }

      onHideSubBoxChange((state: boolean) => {
         if (state) {
            this._successStyle();
         } else {
            this._setDefault();
         }
      });
   }

   protected _getEl(): MooToolsElement {
      return this.el;
   }

   protected _getIconEl(): MooToolsElement {
      const iconClass = 'newsletterIcon';

      let iconEl = this.el.querySelector(`.${iconClass}`) as MooToolsElement | null;

      if (iconEl) {
         return this.el;
      }

      iconEl = document.createElement('span') as unknown as MooToolsElement;
      this.el.append(iconEl);
      iconEl.addClass(iconClass);

      return iconEl;
   }

   protected _getTextEl(): MooToolsElement | null {
      return this.el.querySelector('p') as MooToolsElement | null;
   }

   protected _getEmailEl(): HTMLInputElement | null {
      const form = this._getForm();
      return form ? form.querySelector('input') : null;
   }

   protected _getForm(): HTMLFormElement | null {
      return this.el.querySelector('form');
   }

   protected _getViewLinkEls(): MooToolsElement[] | null {
      return null;
   }

   protected _getEmailSuccessIcon(): string {
      return '';
   }

   protected _getEmailSuccessText(): string {
      return _js(
         'You are a dashing and intelligent person. We look forward to getting to know you.'
      );
   }

   protected _getEmailDefaultText(): string {
      return _js('Learn something new every week:');
   }

   protected _getEmailErrorIcon(): string {
      return '';
   }

   protected _getEmailErrorText(): string {
      return _js('Invalid Email');
   }

   protected _getGenericErrorText(): string {
      return _js('An Unknown Error Occurred. Please try again.');
   }

   protected _getRecaptchaKey(): string {
      return App.recaptchaToken;
   }

   protected _getRecaptchaPlaceholder(): Element {
      const placeholderClass = 'newsletter-recaptcha';
      const emailEl = this._getEmailEl();
      let recaptchaPlaceholder = emailEl?.querySelector(`.${placeholderClass}`);

      if (recaptchaPlaceholder) {
         return recaptchaPlaceholder;
      }

      recaptchaPlaceholder = document.createElement('div');
      (recaptchaPlaceholder as MooToolsElement).addClass(placeholderClass);
      emailEl?.append(recaptchaPlaceholder);
      return recaptchaPlaceholder;
   }

   protected _signup(): Promise<SignupResponse> {
      return this._getRecaptchaPromise().then(recaptchaResponse => {
         const emailEl = this._getEmailEl();
         return new Request.AjaxIO('subscribe', {
            onSuccess: this._signupSuccess.bind(this),
         }).send(emailEl?.value || '', recaptchaResponse);
      });
   }

   protected _signupWithLoading(): Promise<SignupResponse> {
      const signupPromise = this._signup();

      LoadingIndicator.withPromise(signupPromise, {
         pendingMessage: _js('Signing you up'),
         successMessage: _js('Signed up!'),
         failureMessage: _js('Something went wrong.'),
      });

      return signupPromise;
   }

   protected _getRecaptchaPromise(): Promise<string> {
      return Recaptcha.executeInvisible(
         this._getRecaptchaPlaceholder(),
         this._getRecaptchaKey(),
         'newsletter'
      );
   }

   protected _signupSuccess(response: SignupResponse): void {
      const result = typeof response === 'object' ? response.result : response;

      if (result === 'success') {
         this._setSuccess();
      } else {
         this._setError();
      }
   }

   protected _setDefault(): void {
      this.el.removeClass('error');
      this.el.removeClass('success');
      this._getIconEl().removeClass(this._getEmailSuccessIcon());
      this._getTextEl()?.set('text', this._getEmailDefaultText());
      this._getViewLinkEls()?.forEach(link => link.set('text', _js('Let me read it first!')));
   }

   protected _setSuccess(): void {
      setHideSubBox(true);
      let from = 'Footer';
      if (this.el.getAttribute('id') === 'newsletter-sub-box') {
         from = 'Newsletter page';
      }
      this._successStyle();

      this._successAnalytics(from);
   }

   protected _successStyle(): void {}

   protected _successAnalytics(from: string): void {
      trackAnalyticsCustomEvent({
         eventCategory: 'Newsletter',
         eventAction: `Newsletter - Form - ${from} - Submit`,
      });
      fbq('track', 'CompleteRegistration', { content_name: 'Newsletter' });
   }

   protected _setError(): void {
      this.el.addClass('error');
      this._getIconEl().addClass(this._getEmailErrorIcon());
      this._getTextEl()?.set('text', this._getEmailErrorText());
   }

   protected _validate(): boolean {
      const emailEl = this._getEmailEl();
      const isValid = Validation.email(emailEl?.value);
      if (!isValid) {
         this._setError();
      }
      return isValid;
   }

   protected _onSubmit(ev: Event): void {
      ev.stopPropagation();
      ev.preventDefault();
      this._setDefault();

      const isValid = this._validate();
      if (!isValid) {
         return;
      }

      void this._signupWithLoading();
   }
}

class WPNewsletter extends NewsLetter {
   protected hiddenForm: HTMLFormElement;
   protected slug: string;

   constructor(el: Element | null, slug: string) {
      super(null);
      this.slug = slug;

      const hiddenForm = document.createElement('form');
      const hiddenInput = document.createElement('input');
      this.hiddenForm = hiddenForm;

      hiddenForm.style.display = 'none';
      hiddenForm.append(hiddenInput);
      document.body.append(hiddenForm);

      if (el) {
         this.el = el as MooToolsElement;
         const form = this._getForm();
         form?.addEventListener('submit', ev => {
            ev.stopPropagation();
            ev.preventDefault();

            const emailEl = this._getEmailEl();
            hiddenInput.value = emailEl?.value || '';
            hiddenForm.addEventListener('submit', this._onSubmit.bind(this));
            hiddenForm.dispatchEvent(new Event('submit', { cancelable: true }));
         });

         onHideSubBoxChange((state: boolean) => {
            if (state) {
               this._successStyle();
            } else {
               this._setDefault();
            }
         });
      }
   }

   protected override _getRecaptchaPlaceholder(): Element {
      const placeholderClass = 'newsletter-recaptcha';
      let recaptchaPlaceholder = this.hiddenForm.querySelector(`.${placeholderClass}`);

      if (recaptchaPlaceholder) {
         return recaptchaPlaceholder;
      }

      recaptchaPlaceholder = document.createElement('div');
      (recaptchaPlaceholder as MooToolsElement).addClass(placeholderClass);
      this.hiddenForm.append(recaptchaPlaceholder);
      return recaptchaPlaceholder;
   }

   protected override _successAnalytics(): void {
      trackAnalyticsCustomEvent({
         eventCategory: 'Newsletter',
         eventAction: `Newsletter - Form - ${this.slug} - Submit`,
      });
      fbq('track', 'CompleteRegistration', { content_name: 'Newsletter' });
   }

   protected override _getViewLinkEls(): MooToolsElement[] | null {
      return querySelectorAllWithShadowed(
         '.subscribe-form .form-footer-text .link-text'
      ) as MooToolsElement[];
   }

   protected override _successStyle(): void {
      this.el.removeClass('error');
      this.el.addClass('success');
      this._getIconEl().addClass(this._getEmailSuccessIcon());
      this._getTextEl()?.set('text', this._getEmailSuccessText());
      (this._getForm() as unknown as MooToolsElement)?.hide();
      this._getViewLinkEls()?.forEach(link => link.set('text', 'Let me read it!'));
   }
}

class FooterNewsletter extends NewsLetter {
   constructor(el: Element | null) {
      super(el);
   }

   protected override _setDefault(): void {}
}

export default NewsLetter;
export { WPNewsletter, FooterNewsletter };
